<template>
    <div class="rater-table overflow-x-auto" role="table" :aria-label="t('reflectionsAdmin.raterTable.tableLabel')">
        <div class="rater-grid header" role="row">
            <span role="columnheader">{{ t("reflectionsAdmin.raterTable.rater") }}</span>
            <span role="columnheader">{{ t("reflectionsAdmin.raterTable.relationship") }}</span>
            <span role="columnheader">{{ t("reflectionsAdmin.raterTable.status") }}</span>
        </div>
        <div v-for="row in rows" :key="row.invite_id" class="rater-grid row" role="row">
            <div class="flex items-center gap-2.5 min-w-0" role="cell">
                <BaseAvatar size="xs" theme="identity" :initials="row.rater_initials" :identity-key="row.invite_id" />
                <span class="flex flex-col min-w-0">
                    <span class="body-small text-ink break-all">{{ raterName(row) }}</span>
                    <span class="details text-ink-muted truncate">{{ t("reflectionsAdmin.raterTable.rating", { subject: subjectName(row) }) }}</span>
                </span>
            </div>
            <span class="details text-ink-muted truncate" role="cell">{{ relationshipLabel(row.relationship) }}</span>
            <span role="cell"
                ><StatusChip :state="chipState(row.state)" size="xs">{{ stateLabel(row.state) }}</StatusChip></span
            >
        </div>
    </div>
</template>

<script setup lang="ts">
import type { AdminRaterRow, AdminRaterState, ReflectionResponseRelationship } from "~vue/api/generated/reflections-admin";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import StatusChip, { type StatusChipState } from "~vue/components/StatusChip.vue";
import { useI18n } from "vue-i18n";

interface Props {
    rows: AdminRaterRow[];
    // Closed campaigns can't collect any more, so an open invite reads as "No response".
    campaignClosed?: boolean;
}

const { rows, campaignClosed = false } = defineProps<Props>();
const { t } = useI18n();

const relationshipLabels: Record<ReflectionResponseRelationship, () => string> = {
    peer: () => t("reflectionsAdmin.raterTable.relationshipLabel.peer"),
    manager: () => t("reflectionsAdmin.raterTable.relationshipLabel.manager"),
    skip_manager: () => t("reflectionsAdmin.raterTable.relationshipLabel.skipManager"),
    direct: () => t("reflectionsAdmin.raterTable.relationshipLabel.direct"),
    other: () => t("reflectionsAdmin.raterTable.relationshipLabel.other"),
    self: () => t("reflectionsAdmin.raterTable.relationshipLabel.self"),
};

function relationshipLabel(relationship: ReflectionResponseRelationship | null | undefined): string {
    return relationship ? relationshipLabels[relationship]() : t("reflectionsAdmin.raterTable.noRelationship");
}

function raterName(row: AdminRaterRow): string {
    return row.rater_is_former_member ? t("reflectionsAdmin.raterTable.formerMember") : row.rater_name;
}

function subjectName(row: AdminRaterRow): string {
    return row.subject_is_former_member ? t("reflectionsAdmin.raterTable.formerMember") : row.subject_name;
}

function chipState(state: AdminRaterState): StatusChipState {
    return state === "responded" ? "done" : "neutral";
}

// Closing a campaign expires its open invites, so on a closed campaign both
// pending and expired mean the rater never answered.
function stateLabel(state: AdminRaterState): string {
    if (state === "responded") {
        return t("reflectionsAdmin.raterTable.state.responded");
    }
    if (campaignClosed) {
        return t("reflectionsAdmin.raterTable.state.noResponse");
    }
    return state === "expired" ? t("reflectionsAdmin.raterTable.state.expired") : t("reflectionsAdmin.raterTable.state.pending");
}
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .rater-table {
        @apply grid;
        grid-template-columns: minmax(160px, 1fr) max-content max-content;
    }

    .rater-grid {
        @apply grid grid-cols-subgrid col-span-full items-center gap-3 px-2;
    }

    .rater-grid.header {
        @apply scaffold-details-small text-ink-muted pb-2 border-b border-border;
    }

    .rater-grid.row {
        @apply py-3 border-b border-border rounded-md transition-colors hover:bg-surface-hover;
    }
}
</style>
